/**
 * Admin → Contract Pack Setup → Pricing Rates
 *
 * Two values that drive the auto-sum on the Performance Estimate page of
 * every contract pack:
 *
 *   electricity_buy_rate (£/kWh) — used in:
 *     Annual savings  =  self-consumption kWh × electricity_buy_rate
 *
 *   seg_export_rate (£/kWh) — used in:
 *     SEG income      =  annual output kWh × export% × seg_export_rate
 *
 * Changes take effect on the next auto-fill / pack generation across all
 * customers. No deploy needed.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, RotateCcw, PoundSterling, Zap, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ROW_BASE = 'flex flex-col gap-1.5';

export const ContractPricingContent = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({ electricity_buy_rate: 0.28, seg_export_rate: 0.15 });
  const [buyRate, setBuyRate] = useState('0.28');
  const [segRate, setSegRate] = useState('0.15');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/contract-pricing');
      setBuyRate(String(data.electricity_buy_rate));
      setSegRate(String(data.seg_export_rate));
      setDefaults(data.defaults || defaults);
      setUpdatedAt(data.updated_at || null);
      setUpdatedBy(data.updated_by || null);
    } catch (e) {
      toast.error('Failed to load pricing rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    const buy = parseFloat(buyRate);
    const seg = parseFloat(segRate);
    if (Number.isNaN(buy) || Number.isNaN(seg)) {
      toast.error('Please enter numeric values for both rates');
      return;
    }
    if (buy < 0 || buy > 2 || seg < 0 || seg > 2) {
      toast.error('Rates must be between £0 and £2 per kWh');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/admin/contract-pricing', {
        electricity_buy_rate: buy,
        seg_export_rate: seg,
      });
      setUpdatedAt(data.updated_at || null);
      setUpdatedBy(data.updated_by || null);
      toast.success('Pricing rates updated — applies to all contract packs from now on');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save pricing rates');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setBuyRate(String(defaults.electricity_buy_rate));
    setSegRate(String(defaults.seg_export_rate));
    toast.message('Reverted to defaults — click Save to apply');
  };

  // Worked example: highlights why both fields matter so admins don't change
  // them blindly. Reactive to the current values typed (not the saved ones).
  const example = (() => {
    const buy = parseFloat(buyRate) || 0;
    const seg = parseFloat(segRate) || 0;
    const sc = 2925;       // kWh — typical battery-system self-consumption
    const output = 5850;   // kWh — typical 6kWp annual output
    const exportPct = 50;
    const savings = sc * buy;
    const segIncome = output * (exportPct / 100) * seg;
    const total = savings + segIncome;
    return { sc, output, exportPct, savings, segIncome, total };
  })();

  // "X days ago" helper for the Last-changed stamp
  const relTime = (iso) => {
    if (!iso) return null;
    try {
      const dt = new Date(iso);
      const diffMs = Date.now() - dt.getTime();
      const day = 1000 * 60 * 60 * 24;
      const days = Math.floor(diffMs / day);
      if (days <= 0) {
        const hrs = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)));
        return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
      }
      if (days === 1) return 'yesterday';
      if (days < 30) return `${days} days ago`;
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    } catch { return null; }
  };
  const lastChangedLabel = updatedAt ? relTime(updatedAt) : null;

  const body = (
    <div className="space-y-5">
      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex flex-wrap items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-600" />
              Annual Savings rate &amp; SEG export rate
            </span>
            {lastChangedLabel && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-2 py-0.5"
                title={`Last changed: ${new Date(updatedAt).toLocaleString('en-GB')}${updatedBy ? ` by ${updatedBy}` : ''} — full history in Admin → Audit Log`}
                data-testid="pricing-last-changed"
              >
                Last changed: {lastChangedLabel}{updatedBy ? ` by ${updatedBy}` : ''}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={ROW_BASE}>
              <Label htmlFor="buy-rate" className="text-xs font-medium text-slate-700">
                Electricity buy rate (£ per kWh)
              </Label>
              <div className="relative">
                <PoundSterling className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  id="buy-rate"
                  type="number"
                  step="0.001"
                  min="0"
                  max="2"
                  value={buyRate}
                  onChange={(e) => setBuyRate(e.target.value)}
                  className="pl-7 h-9"
                  data-testid="pricing-buy-rate-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Used in <em>Annual savings = self-consumption kWh × this rate</em>.
                Default <code className="bg-slate-100 px-1 rounded">£{defaults.electricity_buy_rate}</code>.
              </p>
            </div>

            <div className={ROW_BASE}>
              <Label htmlFor="seg-rate" className="text-xs font-medium text-slate-700">
                SEG export rate (£ per kWh)
              </Label>
              <div className="relative">
                <PoundSterling className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  id="seg-rate"
                  type="number"
                  step="0.001"
                  min="0"
                  max="2"
                  value={segRate}
                  onChange={(e) => setSegRate(e.target.value)}
                  className="pl-7 h-9"
                  data-testid="pricing-seg-rate-input"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Used in <em>SEG income = output × export% × this rate</em>.
                Default <code className="bg-slate-100 px-1 rounded">£{defaults.seg_export_rate}</code>.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              onClick={save}
              disabled={loading || saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="pricing-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save &amp; apply to all contracts
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetToDefaults}
              disabled={loading || saving}
              data-testid="pricing-reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" /> Revert to defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Send className="w-4 h-4 text-emerald-600" />
            Worked example (live preview)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-600">
            Typical 6 kWp solar + battery system — {example.sc.toLocaleString()} kWh self-consumption,
            {' '}{example.output.toLocaleString()} kWh annual output, {example.exportPct}% exported.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-emerald-200 rounded-md p-2.5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Annual savings</p>
              <p className="text-base font-bold text-emerald-700">
                £{example.savings.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {example.sc.toLocaleString()} × £{parseFloat(buyRate) || 0}
              </p>
            </div>
            <div className="bg-white border border-emerald-200 rounded-md p-2.5">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">SEG income</p>
              <p className="text-base font-bold text-emerald-700">
                £{example.segIncome.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {example.output.toLocaleString()} × {example.exportPct}% × £{parseFloat(segRate) || 0}
              </p>
            </div>
            <div className="bg-emerald-100 border border-emerald-300 rounded-md p-2.5">
              <p className="text-[10px] text-emerald-700 uppercase tracking-wide font-semibold">Total annual benefit</p>
              <p className="text-base font-bold text-emerald-800">
                £{example.total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[10px] text-emerald-700 mt-0.5">drives payback period</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-[11px] text-slate-500 italic flex items-start gap-2">
        <Badge variant="outline" className="text-[10px] flex-shrink-0">heads-up</Badge>
        <span>
          Changes take effect immediately on the <strong>next</strong> contract pack generation.
          Existing already-generated PDFs keep the rate they were generated with — re-preview a
          pack to see the new numbers, or click <em>Generate</em> again to refresh.
        </span>
      </div>
    </div>
  );

  if (embedded) return body;
  return <div className="p-6 max-w-5xl mx-auto">{body}</div>;
};

export default ContractPricingContent;
